/**
 * Team Browser Component - Gaming UI
 * Browse and search all available teams with floating card layout
 */

import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { FaSearch, FaUsers } from "react-icons/fa";
import teamService from "../../services/api/teamService";
import { getUserSports, getAllSports } from "../../services/api/sportsService";
import FloatingCard from "../ui/FloatingCard";
import EntityCard from "../ui/EntityCard";
import FloatingButton from "../ui/FloatingButton";
import FilterBar from "../ui/FilterBar";
import { useUserFilters } from "../../contexts/UserFiltersContext";
import { eid } from "../../utils/entityId";
import { membersLine, onCourtSize } from "../../utils/teamCapacity";
import "../../styles/features/teams.css";

const TeamBrowser = ({ currentUser, showMyTeams = false, onTeamSelect }) => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joinMessage, setJoinMessage] = useState("");
  const [joinError, setJoinError] = useState("");
  const [userSports, setUserSports] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    sport: "",
    status: "active",
    organizationId: "",
    limit: 12,
    offset: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  });

  // Load user's sports for filter; fallback to all active sports
  useEffect(() => {
    const loadUserSports = async () => {
      try {
        const userRes = await getUserSports();
        const data = userRes?.data?.sports || userRes?.sports || userRes?.data || [];
        if (data.length) { setUserSports(data); return; }
      } catch { /* fall through */ }
      try {
        const allRes = await getAllSports();
        setUserSports(allRes?.data || []);
      } catch { /* ignore */ }
    };
    loadUserSports();
  }, []);

  // Sport filter options built from user's sports
  const sportOptions = [
    { value: "", label: "All Sports" },
    ...userSports.map((s) => ({ value: s.name, label: s.name }))
  ];

  const statusOptions = [
    { value: "active", label: "Active Teams" },
    { value: "inactive", label: "Inactive Teams" },
    { value: "", label: "All Teams" },
  ];

  // Fetch teams with current filters
  const { sportTypeParams, filterParams, isFiltersReady, autoExpandRadius, needsLocation } = useUserFilters();

  const fetchTeams = useCallback(async () => {
    if (!isFiltersReady && !showMyTeams) return;
    // No location yet: the FilterBar asks for one; never list every region
    if (needsLocation && !showMyTeams) {
      setTeams((prev) => (prev.length ? [] : prev));
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      // Merge context sport/location params into the request. My Teams are
      // the user's own, wherever they are, so only browsing is local.
      const contextParams = {
        ...sportTypeParams,
        ...(filterParams.lat != null && !showMyTeams ? {
          lat: filterParams.lat,
          lng: filterParams.lng,
          radius: filterParams.radius,
        } : {}),
      };

      let response;
      if (showMyTeams && currentUser) {
        // Fetch user's teams
        response = await teamService.getUserTeams({ ...filters, ...contextParams });
      } else {
        // Fetch all teams
        response = await teamService.getTeams({ ...filters, ...contextParams });
      }

      if (response.success) {
        const teamsData = response.data?.teams || response.data || [];
        const normalizedTeams = Array.isArray(teamsData) ? teamsData : [];
        setTeams(normalizedTeams);
        if (filterParams.lat != null && !showMyTeams) {
          autoExpandRadius(normalizedTeams.length);
        }
        setPagination({
          page: Math.floor(filters.offset / filters.limit) + 1,
          totalPages:
            response.data?.pagination?.totalPages ||
            response.pagination?.totalPages ||
            1,
          total:
            response.data?.pagination?.total || response.pagination?.total || 0,
        });
      } else {
        setError(response.message || "Failed to fetch teams");
      }
    } catch (err) {
      console.error("Error fetching teams:", err);
      setError("Failed to load teams. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [filters, showMyTeams, currentUser, sportTypeParams, filterParams, isFiltersReady, needsLocation, autoExpandRadius]);

  // Fetch teams when component mounts, filters change, or context sport/location changes
  useEffect(() => {
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.sport, filters.status, filters.organizationId, filters.limit, filters.offset, showMyTeams, currentUser?.id, sportTypeParams, filterParams.lat, filterParams.lng, filterParams.radius, isFiltersReady, needsLocation]);

  // Handle search input change
  const handleSearchChange = (e) => {
    const newSearch = e.target.value;
    setFilters((prev) => ({
      ...prev,
      search: newSearch,
      offset: 0, // Reset to first page
    }));
  };

  // Handle filter change
  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
      offset: 0, // Reset to first page
    }));
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    const newOffset = (newPage - 1) * filters.limit;
    setFilters((prev) => ({
      ...prev,
      offset: newOffset,
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      search: "",
      sport: "",
      status: "active",
      organizationId: "",
      limit: 12,
      offset: 0,
    });
  };

  // One flat card per team (demo-ready PR 4): name, sport badge, plain rows
  const TeamCard = ({ team }) => {
    let settings = {};
    if (team.settings) {
      try {
        settings = typeof team.settings === "string" ? JSON.parse(team.settings) : team.settings;
      } catch (error) {
        settings = {};
      }
    }

    const activeCount = team.active_members_count || 0;
    const requiresApproval = team.requires_approval ?? settings.requires_approval ?? false;
    const isPublic = team.is_public ?? settings.is_public ?? settings.public_join ?? false;
    const sport = team.sport_type
      ? team.sport_type.charAt(0).toUpperCase() + team.sport_type.slice(1)
      : "Multi-Sport";
    const openMembers = (event) => {
      event.stopPropagation();
      if (onTeamSelect) {
        onTeamSelect(eid(team), { initialTab: "members" });
      }
    };

    const rows = [
      {
        key: "members",
        icon: "👥",
        label: "Members",
        value: (
          <span
            className="entity-card__link"
            role="button"
            tabIndex={0}
            onClick={openMembers}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openMembers(event);
              }
            }}
            aria-label="View team members"
          >
            {membersLine(activeCount, onCourtSize(team))}
          </span>
        ),
      },
      team.organization_name && { key: "org", icon: "🏢", label: "Organization", value: team.organization_name },
      team.location && { key: "location", icon: "📍", label: "Location", value: team.location },
      { key: "join", icon: "✋", label: "Join", value: requiresApproval ? "Requires Approval" : "Automatic" },
      (team.owner_name || team.owner_username) && {
        key: "owner",
        icon: "👑",
        label: "Owner",
        value: (
          <Link
            to={`/players/${team.created_by}`}
            onClick={(e) => e.stopPropagation()}
            className="entity-card__link"
          >
            {team.owner_name || team.owner_username}
          </Link>
        ),
      },
    ].filter(Boolean);

    return (
      <EntityCard
        title={team.name}
        logoUrl={team.logo_url}
        badges={[
          { label: sport, tone: "sport" },
          { label: isPublic ? "🌐 Public" : "🔒 Private", tone: isPublic ? "success" : "danger" },
        ]}
        rows={rows}
        onClick={() => {
          if (onTeamSelect) {
            onTeamSelect(eid(team));
          }
        }}
      />
    );
  };

  // Handle join team action
  const handleJoinTeam = async (teamId) => {
    if (!currentUser) {
      setJoinError("Please login to join a team");
      setTimeout(() => setJoinError(""), 3000);
      return;
    }

    try {
      setJoinError("");
      setJoinMessage("");
      
      // Find the team to check if it's full
      const team = teams.find(t => t.id === teamId);
      if (team) {
        const maxMembers = team.max_members || team.settings?.maxPlayers;
        const currentMembers = team.active_members_count || 0;
        
        if (maxMembers && currentMembers >= maxMembers) {
          setJoinError(`Team is full (${currentMembers}/${maxMembers} members)`);
          setTimeout(() => setJoinError(""), 5000);
          return;
        }
      }

      // Send join request
      const response = await teamService.joinTeam(teamId, {
        requestedRole: "player",
        message: "I would like to join your team"
      });

      if (response.success) {
        const message = response.data?.status === "pending" 
          ? "Join request sent! Waiting for team approval." 
          : "Successfully joined the team!";
        setJoinMessage(message);
        setTimeout(() => setJoinMessage(""), 5000);
        
        // Refresh teams to update member count
        fetchTeams();
      }
    } catch (err) {
      console.error("Error joining team:", err);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || "Failed to send join request. Please try again.";
      setJoinError(errorMsg);
      setTimeout(() => setJoinError(""), 5000);
    }
  };

  // Loading state
  if (loading && teams.length === 0) {
    return (
      <FloatingCard className="loading-card-gaming">
        <div className="loading-content-gaming">
          <div className="gaming-spinner"></div>
          <p className="loading-text">Loading teams...</p>
        </div>
      </FloatingCard>
    );
  }

  return (
    <div className="team-browser-gaming">
      {/* Success/Error Messages */}
      {joinMessage && (
        <FloatingCard className="alert-card alert-success-gaming">
          <div className="alert-content">
            <span className="alert-icon">✅</span>
            <p className="alert-message">{joinMessage}</p>
          </div>
        </FloatingCard>
      )}

      {joinError && (
        <FloatingCard className="alert-card alert-error-gaming">
          <div className="alert-content">
            <span className="alert-icon">⚠️</span>
            <p className="alert-message">{joinError}</p>
          </div>
        </FloatingCard>
      )}

      {/* Compact Search, Filters & Results */}
      <FloatingCard className="search-controls-compact">
        <FilterBar inline showSports={false} />
        <div className="controls-row-compact">
          {/* Search Section */}
          <div className="search-section-compact">
            <div className="search-input-wrapper-compact">
              <FaSearch className="search-icon-compact" />
              <input
                type="text"
                placeholder="Search teams..."
                value={filters.search}
                onChange={handleSearchChange}
                className="search-input-compact"
              />
            </div>
          </div>

          {/* Inline Filters */}
          <div className="inline-filters-compact">
            <select
              value={filters.sport}
              onChange={(e) => handleFilterChange("sport", e.target.value)}
              className="filter-select-compact"
            >
              {sportOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="filter-select-compact"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {(filters.search ||
              filters.sport ||
              filters.status !== "active") && (
              <FloatingButton
                variant="warning"
                size="small"
                onClick={clearFilters}
                icon="🧹"
              >
                <span className="btn-text">Clear Filters</span>
              </FloatingButton>
            )}
          </div>

          {/* Results Count */}
          <div className="results-count-compact">
            {!error && (
              <span className="count-text-compact">
                <strong>{teams.length}</strong> of{" "}
                <strong>{pagination.total}</strong> teams
              </span>
            )}
          </div>
        </div>
      </FloatingCard>

      {/* Error State */}
      {error && (
        <FloatingCard className="error-card-gaming">
          <div className="error-content-gaming">
            <span className="error-icon">⚠️</span>
            <p className="error-message">{error}</p>
            <button
              onClick={fetchTeams}
              className="floating-btn retry-btn-gaming"
            >
              <span className="btn-icon">🔄</span>
              Try Again
            </button>
          </div>
        </FloatingCard>
      )}

      {/* Sports-Style Teams Grid */}
      {!error && teams.length > 0 && (
        <div className="teams-grid-sports-style">
          {teams.map((team, index) => (
            <div key={team.id} className={`delay-${(index % 5) + 4}`}>
              <TeamCard team={team} />
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && teams.length === 0 && !error && (
        <FloatingCard className="empty-state-gaming">
          <FaUsers className="empty-icon" />
          <h3 className="empty-title">{needsLocation && !showMyTeams ? "Teams near you" : "No Teams Found"}</h3>
          <p className="empty-text">
            {needsLocation && !showMyTeams
              ? "Set your location above to see what's near you."
              : filters.search || filters.sport || filters.organizationId
              ? "No teams match your current filters. Try adjusting your search criteria."
              : "There are no teams available at the moment."}
          </p>
          {(filters.search || filters.sport || filters.organizationId) && (
            <button
              onClick={clearFilters}
              className="floating-btn clear-filters-btn-gaming"
            >
              <span className="btn-icon">🗑️</span>
              Clear Filters
            </button>
          )}
        </FloatingCard>
      )}

      {/* Compact Pagination */}
      {pagination.totalPages > 1 && (
        <FloatingCard className="pagination-compact">
          <div className="pagination-content-compact">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="btn-compact nav-btn"
              title="Previous page"
            >
              ◀️
            </button>

            <div className="page-info-compact">
              Page <strong>{pagination.page}</strong> of{" "}
              <strong>{pagination.totalPages}</strong>
            </div>

            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="btn-compact nav-btn"
              title="Next page"
            >
              ▶️
            </button>
          </div>
        </FloatingCard>
      )}

      {/* Loading overlay for subsequent requests */}
      {loading && teams.length > 0 && (
        <div className="loading-overlay-gaming">
          <div className="gaming-spinner small"></div>
        </div>
      )}
    </div>
  );
};

export default TeamBrowser;
